import {getOfferCheckParams} from 'modules/accommodation/utils'

import {getPersistedSapiLabelWithFallback} from '@vio/core/src/_web/sapiLabel/business'
import {parseUrl} from '@vio/core/src/_web/utils/url'
import {
  buildOfferCheckEndpoint,
  OfferCheckedIn
} from '@vio/core/src/offer/business/buildOfferCheckEndpoint'
import {getLinkByType} from '@vio/core/src/offer/business/getLinkByType'
import {getOfferCheckApiRequestParams} from '@vio/core/src/offer/business/getOfferCheckApiRequestParams'
import {ActionLinkTypes} from '@vio/core/src/offer/types/actionLink'
import {Offer} from '@vio/core/src/offer/types/offer'
import {OfferCheckApiRequestParams} from '@vio/core/src/offer/types/OfferCheck'
import {getRoomlessVersion} from '@vio/core/src/room/business/utils/roomless'
import {Room, SplitBooking} from '@vio/core/src/room/types/room'
import {BaseUrlCheckoutParams} from '@vio/core/src/utils/url/types/UrlParams'

export type BuildOfferCheckApiParamsInput = {
  headers: Record<string, string>
  /** Params from getCommonPaymentPageLandingParams - loosely typed to match selector return */
  commonPaymentPageParams: Record<string, unknown>
  offer?: Offer
  /** Room holding `offer`, read for the roomless version */
  room?: Pick<Room, 'name'>
  splitBooking?: SplitBooking
}

/**
 * Pure function that builds offer check API request params from headers,
 * common payment page params, and either an offer or split booking.
 * Used by both the proactive offer check middleware and the room offer interaction hook.
 */
export const buildOfferCheckApiParams = (
  input: BuildOfferCheckApiParamsInput & {apiKey: string}
): OfferCheckApiRequestParams | null => {
  const {headers, commonPaymentPageParams, offer, room, splitBooking, apiKey} =
    input

  if (!offer && !splitBooking) return null

  const links = offer?.links ?? splitBooking?.links
  const checkoutUrl = getLinkByType(links, ActionLinkTypes.CHECKOUT)
  if (!checkoutUrl) return null

  const {searchObject} = parseUrl(checkoutUrl)
  const offerParams = getOfferCheckParams({offer, splitBooking})
  const label = getPersistedSapiLabelWithFallback()

  const offerCheckUrl = buildOfferCheckEndpoint(
    {
      ...commonPaymentPageParams,
      ...offerParams,
      ...(searchObject as BaseUrlCheckoutParams),
      label,
      rl: offer ? getRoomlessVersion(room) : undefined,
      offerCheckedIn: OfferCheckedIn.AP_OFFER_CLICK
    },
    process.env.NEXT_PUBLIC_BOVIO_ENDPOINT
  )

  return getOfferCheckApiRequestParams(offerCheckUrl, apiKey, headers)
}
